import React, { useRef, useState } from "react";
import { compose } from "redux";
import enhancer from "./client-reset-password-validator";
import Button from "../button/Button";
import Loader from "../common/Loader";
import Toaster from "../common/Toaster";
import "react-table/react-table.css";
import ClientServices from "../../api/ClientServices";
import EncryptDecrypt from "../../api/EncryptDecrypt";
import showPwdImg from "../../assets/images/show-password.svg";
import hidePwdImg from "../../assets/images/hide-password.svg";

const parseResetPasswordResponse = raw => {
  if (raw == null) return {};
  if (typeof raw === "string") {
    return EncryptDecrypt.decrypt(raw);
  }
  return raw;
};

const ClientResetPasswordModal = ({
  clientId,
  adminId,
  toggle,
  toasterRef,
  ...props
}) => {
  const {
    handleChange,
    handleBlur,
    errors,
    touched,
    submitCount,
    isValid,
    handleSubmit,
    values
  } = props;

  const fallbackToaster = useRef(null);
  const toaster = toasterRef || fallbackToaster;
  const [isRevealNewPwd, setIsRevealNewPwd] = useState(false);
  const [isRevealConfirmPwd, setIsRevealConfirmPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const Error = fieldProps => {
    const field1 = fieldProps.field;
    if ((errors[field1] && touched[field1]) || submitCount > 0) {
      return (
        <span className={fieldProps.class ? fieldProps.class : "error-msg"}>
          {errors[field1]}
        </span>
      );
    }
    return <span />;
  };

  const onFormSubmit = e => {
    e.preventDefault();
    if (isValid) {
      setLoading(true);
      const payload = {
        userId: Number(clientId),
        newPassword: values.newPassword,
        resetBy: String(adminId)
      };
      ClientServices.resetUserPassword(payload)
        .then(res => {
          const body = parseResetPasswordResponse(res.data);
          const successMessage =
            (body.data && body.data.message) || body.message || "";
          const isOk =
            (body.status === 1 || body.status === true) &&
            body.data &&
            body.data.success;
          if (isOk) {
            if (toaster.current) {
              toaster.current.success(
                successMessage || "Password reset successfully"
              );
            }
            toggle();
          } else if (toaster.current) {
            toaster.current.error(body.message || "Request failed");
          }
        })
        .catch(err => {
          if (toaster.current) {
            if (err.response && err.response.data) {
              const data = parseResetPasswordResponse(err.response.data);
              toaster.current.error((data && data.message) || "Request failed");
            } else {
              toaster.current.error(
                err.message ||
                  "Network error. Check your connection and try again."
              );
            }
          }
          toggle();
        })
        .finally(() => {
          setLoading(false);
        });
    }
    handleSubmit();
  };

  return (
    <>
      <div
        className="ReactTable"
        style={{
          position: "relative",
          border: "none",
          boxShadow: "none",
          minHeight: loading ? 260 : undefined
        }}
      >
        <Loader loading={loading} />
        <form onSubmit={onFormSubmit}>
          <div className="form-group">
            <label className="fs-16 medium-text" htmlFor="resetNewPassword">
              New Password<span className="asterisk"> *</span>
            </label>
            <div style={{ position: "relative" }}>
              <input
                type={isRevealNewPwd ? "text" : "password"}
                onPaste={e => {
                  e.preventDefault();
                }}
                className="form-control react-form-input"
                id="resetNewPassword"
                name="newPassword"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.newPassword}
                placeholder="New Password"
                style={{ paddingRight: "30px" }}
                autoComplete="new-password"
              />
              <img
                style={{
                  cursor: "pointer",
                  position: "absolute",
                  width: "20px",
                  right: "8px",
                  top: "8px"
                }}
                title={isRevealNewPwd ? "Hide password" : "Show password"}
                src={isRevealNewPwd ? showPwdImg : hidePwdImg}
                alt=""
                onClick={() => setIsRevealNewPwd(prev => !prev)}
              />
            </div>
            <Error field="newPassword" />
          </div>
          <div className="form-group">
            <label className="fs-16 medium-text" htmlFor="resetConfirmPassword">
              Confirm Password<span className="asterisk"> *</span>
            </label>
            <div style={{ position: "relative" }}>
              <input
                type={isRevealConfirmPwd ? "text" : "password"}
                onPaste={e => {
                  e.preventDefault();
                }}
                className="form-control react-form-input"
                id="resetConfirmPassword"
                name="confirmPassword"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.confirmPassword}
                placeholder="Confirm Password"
                style={{ paddingRight: "30px" }}
                autoComplete="new-password"
              />
              <img
                style={{
                  cursor: "pointer",
                  position: "absolute",
                  width: "20px",
                  right: "8px",
                  top: "8px"
                }}
                title={isRevealConfirmPwd ? "Hide password" : "Show password"}
                src={isRevealConfirmPwd ? showPwdImg : hidePwdImg}
                alt=""
                onClick={() => setIsRevealConfirmPwd(prev => !prev)}
              />
            </div>
            <Error field="confirmPassword" />
          </div>
          <div className="form-group">
            <div className="custom-control custom-checkbox">
              <input
                type="checkbox"
                className="custom-control-input"
                id="confirmReset"
                name="confirmReset"
                checked={values.confirmReset}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <label
                className="custom-control-label fs-16 medium-text"
                htmlFor="confirmReset"
              >
                I confirm that I want to reset this client&apos;s password
                <span className="asterisk"> *</span>
              </label>
            </div>
            <Error field="confirmReset" />
          </div>
          <div className="flex-x center pt-4">
            <Button
              type="submit"
              className="c-btn c-info form-button fs-16 demi-bold-text mr-10"
              style={{ maxWidth: "125px" }}
              disabled={loading}
            >
              Submit
            </Button>
            <Button
              type="button"
              className="c-btn c-secondary form-button fs-16 demi-bold-text"
              style={{ maxWidth: "125px" }}
              onClick={toggle}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
      {!toasterRef && <Toaster ref={fallbackToaster} />}
    </>
  );
};

export default compose(enhancer)(ClientResetPasswordModal);
